import {
  Room,
  type ClientMessage,
  type LeagueRow,
  type RaceResult,
  type RecentRace,
  type RoomHost,
  type ServerMessage,
} from '@typing-racer/shared';

export type Status = 'connecting' | 'open' | 'closed';

export interface Transport {
  readonly kind: 'online' | 'local';
  connect(): void;
  close(): void;
  send(msg: ClientMessage): void;
  onMessage(cb: (msg: ServerMessage) => void): void;
  onStatus(cb: (s: Status) => void): void;
}

export class WsTransport implements Transport {
  readonly kind = 'online';
  private ws: WebSocket | null = null;
  private msgCb: ((m: ServerMessage) => void) | null = null;
  private statusCb: ((s: Status) => void) | null = null;
  private closed = false;
  private attempts = 0;
  private queue: string[] = [];
  private retry: ReturnType<typeof setTimeout> | null = null;

  connect(): void {
    this.closed = false;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.statusCb?.('connecting');
    const ws = new WebSocket(`${proto}://${location.host}/ws`);
    this.ws = ws;
    ws.addEventListener('open', () => {
      if (this.closed || this.ws !== ws) return;
      this.attempts = 0;
      this.statusCb?.('open');
      for (const q of this.queue) ws.send(q);
      this.queue = [];
    });
    ws.addEventListener('message', (e) => {
      if (this.closed || this.ws !== ws) return;
      try {
        this.msgCb?.(JSON.parse(String(e.data)) as ServerMessage);
      } catch {
        /* ignore */
      }
    });
    ws.addEventListener('close', () => {
      if (this.closed || this.ws !== ws) return;
      this.statusCb?.('closed');
      const delay = Math.min(10_000, 500 * 2 ** this.attempts++);
      this.retry = setTimeout(() => {
        this.retry = null;
        if (!this.closed) this.connect();
      }, delay);
    });
    ws.addEventListener('error', () => ws.close());
  }

  close(): void {
    this.closed = true;
    if (this.retry) clearTimeout(this.retry);
    this.retry = null;
    this.queue = [];
    this.ws?.close();
    this.ws = null;
  }

  send(msg: ClientMessage): void {
    const data = JSON.stringify(msg);
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(data);
    else if (msg.type === 'join' || msg.type === 'createRoom') this.queue.push(data);
  }

  onMessage(cb: (m: ServerMessage) => void): void {
    this.msgCb = cb;
  }
  onStatus(cb: (s: Status) => void): void {
    this.statusCb = cb;
  }
}

/** In-browser room for offline practice. League persists in localStorage. */
export class LocalTransport implements Transport {
  readonly kind = 'local';
  private room: Room;
  private msgCb: ((m: ServerMessage) => void) | null = null;
  private statusCb: ((s: Status) => void) | null = null;
  private id = 'local';

  constructor() {
    const host: RoomHost = {
      now: () => Date.now(),
      send: (_id, msg) => queueMicrotask(() => this.msgCb?.(msg)),
      broadcast: (msg) => queueMicrotask(() => this.msgCb?.(msg)),
      recordRace: (results) => recordLocalRace(results),
      getLeague: () => ({ type: 'league', ...loadLocalLeague() }),
    };
    this.room = new Room(host);
  }

  connect(): void {
    this.statusCb?.('open');
  }
  close(): void {
    this.room.leave(this.id);
    this.room.dispose();
    this.statusCb?.('closed');
  }
  send(msg: ClientMessage): void {
    this.room.handle(this.id, msg);
  }
  onMessage(cb: (m: ServerMessage) => void): void {
    this.msgCb = cb;
  }
  onStatus(cb: (s: Status) => void): void {
    this.statusCb = cb;
  }
}

// ---------------------------------------------------------------- local league
const LEAGUE_KEY = 'typing-racer.league';
interface LocalLeague {
  players: Record<string, LeagueRow & { sumWpm: number; sumAcc: number }>;
  recent: RecentRace[];
  nextId: number;
}

function loadRaw(): LocalLeague {
  try {
    const raw = localStorage.getItem(LEAGUE_KEY);
    if (raw) return JSON.parse(raw) as LocalLeague;
  } catch {
    /* ignore */
  }
  return { players: {}, recent: [], nextId: 1 };
}

function loadLocalLeague(): { rows: LeagueRow[]; recent: RecentRace[] } {
  const l = loadRaw();
  const rows = Object.values(l.players)
    .map((p) => ({ ...p, avgWpm: Math.round((p.sumWpm / p.races) * 10) / 10, avgAccuracy: Math.round((p.sumAcc / p.races) * 1000) / 1000 }))
    .sort((a, b) => b.points - a.points || b.bestWpm - a.bestWpm);
  return { rows, recent: l.recent };
}

function recordLocalRace(results: RaceResult[]): void {
  const humans = results.filter((r) => !r.isBot);
  if (!humans.length) return;
  const l = loadRaw();
  const now = new Date().toISOString();
  for (const r of humans) {
    const key = r.name.toLowerCase();
    const humanPlace = humans.filter((h) => h.place < r.place).length + 1;
    const p = l.players[key] ?? {
      name: r.name, races: 0, wins: 0, podiums: 0, points: 0, bestWpm: 0, avgWpm: 0, avgAccuracy: 0, lastPlayed: now, sumWpm: 0, sumAcc: 0,
    };
    p.name = r.name;
    p.races++;
    p.wins += humanPlace === 1 ? 1 : 0;
    p.podiums += humanPlace <= 3 ? 1 : 0;
    p.points += r.points;
    p.bestWpm = Math.max(p.bestWpm, r.summary.wpm);
    p.sumWpm += r.summary.wpm;
    p.sumAcc += r.summary.accuracy;
    p.lastPlayed = now;
    l.players[key] = p;
  }
  l.recent.unshift({
    id: l.nextId++,
    playedAt: now,
    winner: results.find((r) => r.place === 1)?.name ?? '',
    players: humans.length,
    bestWpm: Math.max(...humans.map((h) => h.summary.wpm)),
  });
  l.recent = l.recent.slice(0, 10);
  try {
    localStorage.setItem(LEAGUE_KEY, JSON.stringify(l));
  } catch {
    /* ignore */
  }
}
